import { useState, useEffect, useMemo, useRef } from 'react';
import {
  listOrgCollection,
  readOrgFile,
  writeOrgFile,
  deleteOrgFile,
} from '../../storage/FileSystemAdapter';
import PeopleSelector, { resolvePerson } from '../shared/PeopleSelector';
import AISuggestButton from '../shared/AISuggestButton';
import { appendChangeLog, isRecordImmutable } from '../../utils/changeLog';
import { syncBidirectionalRefs } from '../../utils/relationSync';
import { scanReferencesToEntity } from '../../utils/intelligence';
import EntitySelector from '../shared/EntitySelector';
import TagInput from '../shared/TagInput';
import SOA_CONTROLS from '../../data/soa-controls';
import { BG_SUBTLE, BG_CONTENT, BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, BTN_DESTRUCTIVE_FILLED, BTN_STATUS_FORWARD, TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, TEXT_DEFAULT, BG_SURFACE, BRAND_PRIMARY, BRAND_PRIMARY_DARK, ERROR_TEXT, ERROR_BG, ERROR_BORDER, BG_MUTED, STATUS_GOOD, BG_SHELL, STATUS_BAD, STATUS_WARN, STATUS_NEUTRAL, FIELD_ERROR_BORDER, UNSAVED_AMBER, SURFACE_WHITE, FONT_PAGE_TITLE, FONT_SECTION_TITLE, FONT_BODY, FONT_BODY_STRONG, FONT_BUTTON, FONT_LIST_ITEM, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_STAT_VALUE } from '../../theme';
import { dateStatus } from '../../utils/dateStatus';
import { ISO_CLAUSES } from '../../iso-clauses';
import ConfirmDialog from '../shared/ConfirmDialog';
import { callWorker } from '../../utils/workerClient';
import { NC_STATUSES } from '../../utils/enums';

// ─── §11 RULE 42 — BLOCK DIALOG SUPPORT ──────────────────────────────────────
const HOLDER_NAV = {
  'risk': 'risks', 'asset': 'assets', 'supplier': 'suppliers',
  'incident': 'incidents', 'audit': 'audits', 'nonconformity': 'nonconformities',
  'objective': 'objectives', 'metric': 'metrics', 'document': 'documents',
  'legal-register': 'legal-register', 'management-review': 'management-reviews',
  'soa-control': 'soa', 'scope': 'scope',
};
function holderLabel(t, holderType, count) {
  const key = `deleteBlockedHolder_${holderType}_${count === 1 ? 'one' : 'other'}`;
  return t[key] || holderType;
}
function summariseHits(hits, t) {
  const counts = {};
  hits.forEach(h => { counts[h.holderType] = (counts[h.holderType] || 0) + 1; });
  return Object.entries(counts).map(([type, n]) => `${n} ${holderLabel(t, type, n)}`).join(', ');
}

// ── CONSTANTS ─────────────────────────────────────────────────────────────────
const NC_SOURCES     = ['Internal Audit', 'External Audit', 'Incident', 'Management Review', 'Awareness gap', 'Other'];
const NC_CATEGORIES  = ['Major', 'Minor', 'Observation'];

const NC_CATEGORY_LABELS = {
  es: { Major: 'Mayor', Minor: 'Menor', Observation: 'Observación' },
  en: { Major: 'Major', Minor: 'Minor', Observation: 'Observation' },
};
const CA_STATUSES = ['Pending', 'In Progress', 'Completed'];

const NC_STATUS_COLORS = {
  'Open':        { bg: ERROR_BG, text: ERROR_TEXT },
  'In Progress': { bg: STATUS_WARN.bg, text: STATUS_WARN.text },
  'Closed':      { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK },
};

const CA_STATUS_COLORS = {
  'Pending':     { bg: BG_MUTED, text: TEXT_MUTED },
  'In Progress': { bg: STATUS_WARN.bg, text: STATUS_WARN.text },
  'Completed':   { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK },
};

const SOURCE_LABELS = {
  es: {
    'Internal Audit':    'Auditoría interna',
    'External Audit':    'Auditoría externa',
    'Incident':          'Incidente',
    'Management Review': 'Revisión por la dirección',
    'Awareness gap':     'Brecha de concienciación',
    'Other':             'Otro',
  },
  en: {
    'Internal Audit':    'Internal Audit',
    'External Audit':    'External Audit',
    'Incident':          'Incident',
    'Management Review': 'Management Review',
    'Awareness gap':     'Awareness gap',
    'Other':             'Other',
  },
};

const NC_STATUS_LABELS = {
  es: { 'Open': 'Abierta', 'In Progress': 'En progreso', 'Closed': 'Cerrada' },
  en: { 'Open': 'Open',    'In Progress': 'In Progress', 'Closed': 'Closed'  },
};

const CA_STATUS_LABELS = {
  es: { 'Pending': 'Pendiente', 'In Progress': 'En progreso', 'Completed': 'Completada' },
  en: { 'Pending': 'Pending',   'In Progress': 'In Progress', 'Completed': 'Completed'  },
};

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    title:           'No conformidades',
    newNC:           '+ Nueva no conformidad',
    save:            'Guardar',
    saving:          'Guardando…',
    saveOk:          '✓ Guardado',
    saveError:       'Error al guardar',
    unsavedLabel:    'Cambios sin guardar',
    unsavedConfirm:  'Hay cambios sin guardar. ¿Salir de todas formas?',
    leaveWithoutSaving: 'Salir sin guardar',
    saveAndLeave:    'Guardar y salir',
    loading:         'Cargando…',
    cntTotal:        'Total',
    cntOpen:         'Abiertas',
    cntInProgress:   'En progreso',
    cntClosed:       'Cerradas',
    editBtn:         'Editar',
    viewBtn:         'Ver',
    deleteBtn:       'Eliminar',
    deleteFormBtn:   'Eliminar no conformidad',
    deleteConfirm:   '¿Eliminar esta no conformidad? Esta acción no se puede deshacer.',
    emptyTitle:      'No hay no conformidades registradas.',
    emptyHint:       'Crea la primera con el botón "+ Nueva no conformidad".',
    filterStatus:    'Estado',
    filterSource:    'Fuente',
    filterOwner:     'Responsable',
    filterClauseControl: 'Cláusula / Control',
    filterAll:       'Todos',
    filterNSelected: '{n} seleccionados',
    filterNoSelection: 'Todos',
    colRef:          'Referencia',
    colDate:         'FECHA',
    colSource:       'FUENTE',
    colClause:       'CLÁUSULA / CONTROL',
    colOwner:        'RESPONSABLE',
    colStatus:       'ESTADO',
    colActions:      'ACCIONES',
    selectAll:             'Seleccionar todo',
    nSelected:             '{n} seleccionados',
    deleteSelected:        'Eliminar seleccionados',
    deleteSelectedConfirm: '¿Eliminar las siguientes no conformidades?\n\n{names}\n\nEsta acción no se puede deshacer.',
    backToList:      '← Volver a la lista',
    formTitleNew:    'Nueva no conformidad',
    formTitleEdit:   'Editar no conformidad',
    sectionGeneral:  'Información general',
    sectionDesc:     'Descripción y acción inmediata',
    sectionRC:       'Causas raíz',
    sectionCAs:      'Acciones correctivas',
    sectionRel:      'Relaciones',
    sectionEffect:   'Revisión de eficacia',
    sectionSt:       'Estado',
    fParentNC:       'No conformidad padre (ref)',
    fParentNCPh:     'p. ej. NC-2026-001',
    fPreventive:     'Acciones preventivas consideradas',
    fContainment:    'Fecha de contención',
    fLinkedRisks:    'Riesgos vinculados',
    riskPlaceholder: 'Buscar riesgo…',
    fLinkedIncidents:    'Incidentes vinculados (IDs)',
    incidentPlaceholder: 'Introducir ID de incidente y pulsar Enter…',
    fRef:            'Referencia',
    fDate:           'Fecha',
    fOwner:          'Responsable',
    fSource:         'Fuente',
    fClause:         'Referencia de cláusula',
    fControl:        'Control del Anexo A',
    fAuditId:        'Auditoría (opcional)',
    fClausePh:       'Buscar cláusula…',
    fControlPh:      'Buscar control…',
    clauseOrControlRequired: 'Debes indicar al menos una cláusula o un control del Anexo A.',
    noAudits:        'Sin auditorías disponibles',
    auditPickNone:   '—',
    fDesc:           'Descripción de la no conformidad',
    fCategory:            'Categoría',
    fRepeatFinding:       'Hallazgo recurrente',
    fVerifiedBy:          'Verificado por',
    fCorrections:         'Correcciones',
    fCorrectionDescription: 'Descripción de la corrección',
    fCorrectionResponsible: 'Responsable',
    fCorrectionDate:      'Fecha de compleción',
    addCorrection:        '+ Añadir corrección',
    dCorrections:         'Correcciones',
    fRCPh:           'Añadir causa raíz y pulsar Enter…',
    fEffectReview:   'Revisión de eficacia',
    fEffectDate:     'Fecha de revisión de eficacia',
    fStatus:         'Estado',
    fClosedDate:     'Fecha de cierre',
    caColAction:     'ACCIÓN',
    caColResp:       'RESPONSABLE',
    caColDeadline:   'FECHA LÍMITE',
    caColStatus:     'ESTADO',
    caFAction:       'Acción',
    caFResp:         'Responsable',
    caFDeadline:     'Fecha límite',
    caAddBtn:        'Añadir',
    caDelConfirm:    '¿Eliminar esta acción correctiva?',
    noRC:            'Sin causas raíz.',
    noCAs:           'Sin acciones correctivas.',
    formSave:        'Guardar',
    formCancel:      'Cancelar',
    validationError: 'Por favor completa los campos obligatorios marcados con *.',
    closeBtn:        'Cerrar no conformidad',
    closingBtn:      'Cerrando…',
    closeError:      'Todas las acciones correctivas deben estar "Completadas" antes de cerrar.',
    dAuditRef:       'Auditoría:',
    dImmediate:      'Correcciones',
    dRC:             'Causas raíz',
    dCAs:            'Acciones correctivas',
    dEffect:         'Revisión de eficacia',
    dEffectDate:     'Fecha de revisión de eficacia:',
    dClosedDate:     'Fecha de cierre:',
    aiError:         'No se pudo obtener sugerencia. Inténtalo de nuevo.',
    aiErrorOverloaded: 'Servidores ocupados, inténtalo de nuevo en un momento.',
    aiConsentRequired: 'Activa el procesamiento con IA en Configuración para usar esta función.',
    required:          '*',
    requiredLegend:    '* Campos obligatorios',
    frozenBanner:      '🔒 Este registro es de solo lectura. Para modificarlo, reabre desde Ajustes → Reabrir registros cerrados.',
    badgeOverdue:      'Vencido',
    badgeSoon:         'Vence pronto',
    deleteBlockedTitle:        'No se puede eliminar: existen referencias',
    deleteBlockedReferencedBy: 'Referenciado por',
    deleteBlockedOpenHolder:   'Abrir',
    deactivateInstead:         'Desactivar en su lugar',
    bulkDeleteSummary_allBlocked: 'Ninguno de los registros seleccionados puede eliminarse porque todos están referenciados por otros registros.',
    bulkDeleteSummary_mixed:      '{deletable} de {total} registros pueden eliminarse; {blocked} están bloqueados por referencias.',
    continueWithDeletables:       'Eliminar los registros no bloqueados',
    deleteBlockedHolder_risk_one:               'riesgo',
    deleteBlockedHolder_risk_other:             'riesgos',
    deleteBlockedHolder_asset_one:              'activo',
    deleteBlockedHolder_asset_other:            'activos',
    deleteBlockedHolder_supplier_one:           'proveedor',
    deleteBlockedHolder_supplier_other:         'proveedores',
    deleteBlockedHolder_incident_one:           'incidente',
    deleteBlockedHolder_incident_other:         'incidentes',
    deleteBlockedHolder_audit_one:              'auditoría',
    deleteBlockedHolder_audit_other:            'auditorías',
    deleteBlockedHolder_nonconformity_one:      'no conformidad',
    deleteBlockedHolder_nonconformity_other:    'no conformidades',
    deleteBlockedHolder_objective_one:          'objetivo',
    deleteBlockedHolder_objective_other:        'objetivos',
    deleteBlockedHolder_metric_one:             'métrica',
    deleteBlockedHolder_metric_other:           'métricas',
    deleteBlockedHolder_document_one:           'documento',
    deleteBlockedHolder_document_other:         'documentos',
    'deleteBlockedHolder_legal-register_one':   'entrada del registro legal',
    'deleteBlockedHolder_legal-register_other': 'entradas del registro legal',
    'deleteBlockedHolder_management-review_one':   'revisión por la dirección',
    'deleteBlockedHolder_management-review_other': 'revisiones por la dirección',
    'deleteBlockedHolder_soa-control_one':      'control del SoA',
    'deleteBlockedHolder_soa-control_other':    'controles del SoA',
    deleteBlockedHolder_scope_one:              'alcance',
    deleteBlockedHolder_scope_other:            'alcance',
  },
  en: {
    title:           'Nonconformities',
    newNC:           '+ New nonconformity',
    save:            'Save',
    saving:          'Saving…',
    saveOk:          '✓ Saved',
    saveError:       'Save error',
    unsavedLabel:    'Unsaved changes',
    unsavedConfirm:  'You have unsaved changes. Leave anyway?',
    leaveWithoutSaving: 'Leave without saving',
    saveAndLeave:    'Save and leave',
    loading:         'Loading…',
    cntTotal:        'Total',
    cntOpen:         'Open',
    cntInProgress:   'In Progress',
    cntClosed:       'Closed',
    editBtn:         'Edit',
    viewBtn:         'View',
    deleteBtn:       'Delete',
    deleteFormBtn:   'Delete nonconformity',
    deleteConfirm:   'Delete this nonconformity? This cannot be undone.',
    emptyTitle:      'No nonconformities recorded.',
    emptyHint:       'Create the first using the "+ New nonconformity" button.',
    filterStatus:    'Status',
    filterSource:    'Source',
    filterOwner:     'Owner',
    filterClauseControl: 'Clause / Control',
    filterAll:       'All',
    filterNSelected: '{n} selected',
    filterNoSelection: 'All',
    colRef:          'Reference',
    colDate:         'DATE',
    colSource:       'SOURCE',
    colClause:       'CLAUSE / CONTROL',
    colOwner:        'OWNER',
    colStatus:       'STATUS',
    colActions:      'ACTIONS',
    selectAll:             'Select all',
    nSelected:             '{n} selected',
    deleteSelected:        'Delete selected',
    deleteSelectedConfirm: 'Delete the following nonconformities?\n\n{names}\n\nThis cannot be undone.',
    backToList:      '← Back to list',
    formTitleNew:    'New nonconformity',
    formTitleEdit:   'Edit nonconformity',
    sectionGeneral:  'General information',
    sectionDesc:     'Description and immediate action',
    sectionRC:       'Root causes',
    sectionCAs:      'Corrective actions',
    sectionRel:      'Relationships',
    sectionEffect:   'Effectiveness review',
    sectionSt:       'Status',
    fParentNC:       'Parent nonconformity (ref)',
    fParentNCPh:     'e.g. NC-2026-001',
    fPreventive:     'Preventive actions considered',
    fContainment:    'Containment date',
    fLinkedRisks:    'Linked risks',
    riskPlaceholder: 'Search risk…',
    fLinkedIncidents:    'Linked incidents (IDs)',
    incidentPlaceholder: 'Enter incident ID and press Enter…',
    fRef:            'Reference',
    fDate:           'Date',
    fOwner:          'Owner',
    fSource:         'Source',
    fClause:         'Clause reference',
    fControl:        'Annex A control',
    fAuditId:        'Audit (optional)',
    fClausePh:       'Search clause…',
    fControlPh:      'Search control…',
    clauseOrControlRequired: 'You must indicate at least one clause or Annex A control.',
    noAudits:        'No audits available',
    auditPickNone:   '—',
    fDesc:           'Nonconformity description',
    fCategory:            'Category',
    fRepeatFinding:       'Repeat finding',
    fVerifiedBy:          'Verified by',
    fCorrections:         'Corrections',
    fCorrectionDescription: 'Correction description',
    fCorrectionResponsible: 'Responsible',
    fCorrectionDate:      'Completion date',
    addCorrection:        '+ Add correction',
    dCorrections:         'Corrections',
    fRCPh:           'Add root cause and press Enter…',
    fEffectReview:   'Effectiveness review',
    fEffectDate:     'Effectiveness review date',
    fStatus:         'Status',
    fClosedDate:     'Closed date',
    caColAction:     'ACTION',
    caColResp:       'RESPONSIBLE',
    caColDeadline:   'DEADLINE',
    caColStatus:     'STATUS',
    caFAction:       'Action',
    caFResp:         'Responsible',
    caFDeadline:     'Deadline',
    caAddBtn:        'Add',
    caDelConfirm:    'Delete this corrective action?',
    noRC:            'No root causes.',
    noCAs:           'No corrective actions.',
    formSave:        'Save',
    formCancel:      'Cancel',
    validationError: 'Please complete the required fields marked with *.',
    closeBtn:        'Close nonconformity',
    closingBtn:      'Closing…',
    closeError:      'All corrective actions must be "Completed" before closing.',
    dAuditRef:       'Audit:',
    dImmediate:      'Corrections',
    dRC:             'Root causes',
    dCAs:            'Corrective actions',
    dEffect:         'Effectiveness review',
    dEffectDate:     'Effectiveness review date:',
    dClosedDate:     'Closed date:',
    aiError:         'Could not get suggestion. Please try again.',
    aiErrorOverloaded: 'Servers are busy, please try again in a moment.',
    aiConsentRequired: 'Enable AI processing in Settings to use this feature.',
    required:          '*',
    requiredLegend:    '* Required fields',
    frozenBanner:      '🔒 This record is read-only. To modify it, reopen from Settings → Reopen closed records.',
    badgeOverdue:      'Overdue',
    badgeSoon:         'Due soon',
    deleteBlockedTitle:        'Cannot delete: references found',
    deleteBlockedReferencedBy: 'Referenced by',
    deleteBlockedOpenHolder:   'Open',
    deactivateInstead:         'Deactivate instead',
    bulkDeleteSummary_allBlocked: 'None of the selected records can be deleted because all of them are referenced by other records.',
    bulkDeleteSummary_mixed:      '{deletable} of {total} records can be deleted; {blocked} are blocked by references.',
    continueWithDeletables:       'Delete the unblocked records',
    deleteBlockedHolder_risk_one:               'risk',
    deleteBlockedHolder_risk_other:             'risks',
    deleteBlockedHolder_asset_one:              'asset',
    deleteBlockedHolder_asset_other:            'assets',
    deleteBlockedHolder_supplier_one:           'supplier',
    deleteBlockedHolder_supplier_other:         'suppliers',
    deleteBlockedHolder_incident_one:           'incident',
    deleteBlockedHolder_incident_other:         'incidents',
    deleteBlockedHolder_audit_one:              'audit',
    deleteBlockedHolder_audit_other:            'audits',
    deleteBlockedHolder_nonconformity_one:      'nonconformity',
    deleteBlockedHolder_nonconformity_other:    'nonconformities',
    deleteBlockedHolder_objective_one:          'objective',
    deleteBlockedHolder_objective_other:        'objectives',
    deleteBlockedHolder_metric_one:             'metric',
    deleteBlockedHolder_metric_other:           'metrics',
    deleteBlockedHolder_document_one:           'document',
    deleteBlockedHolder_document_other:         'documents',
    'deleteBlockedHolder_legal-register_one':   'legal register entry',
    'deleteBlockedHolder_legal-register_other': 'legal register entries',
    'deleteBlockedHolder_management-review_one':   'management review',
    'deleteBlockedHolder_management-review_other': 'management reviews',
    'deleteBlockedHolder_soa-control_one':      'SoA control',
    'deleteBlockedHolder_soa-control_other':    'SoA controls',
    deleteBlockedHolder_scope_one:              'scope',
    deleteBlockedHolder_scope_other:            'scope',
  },
};

// ── HELPERS ───────────────────────────────────────────────────────────────────
function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function genId(seq) {
  return `nc-${todayStr().replace(/-/g, '')}-${String(seq).padStart(3, '0')}`;
}

function genRef(seq, ncPrefix) {
  return `${ncPrefix}-${new Date().getFullYear()}-${String(seq).padStart(3, '0')}`;
}

function blankForm(seq, ncPrefix) {
  return {
    id: genId(seq), ref: genRef(seq, ncPrefix),
    source: 'Internal Audit', auditId: '',
    date: todayStr(), owner: '', clauseRefs: [], linkedControlIds: [],
    category: 'Minor', repeatFinding: false,
    parentNonconformityId: null,
    description: '', corrections: [],
    containmentDate: '',
    rootCauses: [], correctiveActions: [],
    preventiveActionsConsidered: '',
    effectivenessReview: '', effectivenessDate: '',
    verifiedBy: '',
    linkedRiskIds: [],
    linkedIncidentIds: [],
    norms: [],
    status: 'Open', closedDate: '',
    createdAt: new Date().toISOString(),
  };
}

function ncToForm(nc) {
  return {
    id:                  nc.id,
    ref:                 nc.ref                  || '',
    source:              nc.source               || 'Internal Audit',
    auditId:             nc.auditId              || '',
    date:                nc.date                 || '',
    owner:               nc.owner                || '',
    clauseRefs:          Array.isArray(nc.clauseRefs)
      ? nc.clauseRefs
      : (nc.clauseRef || '').split(',').map(x => x.trim()).filter(Boolean),
    linkedControlIds:    Array.isArray(nc.linkedControlIds)
      ? nc.linkedControlIds
      : (nc.controlId ? [nc.controlId.trim()].filter(Boolean) : []),
    category:            nc.category             || 'Minor',
    repeatFinding:       !!nc.repeatFinding,
    parentNonconformityId: nc.parentNonconformityId || null,
    description:         nc.description          || '',
    corrections:         Array.isArray(nc.corrections) ? nc.corrections : [],
    containmentDate:     nc.containmentDate      || '',
    rootCauses:          Array.isArray(nc.rootCauses)         ? nc.rootCauses         : [],
    correctiveActions:   Array.isArray(nc.correctiveActions)
      ? nc.correctiveActions.map((ca) => ({
          ...ca,
          responsible: Array.isArray(ca.responsible)
            ? ca.responsible
            : (ca.responsible ? [ca.responsible] : []),
        }))
      : [],
    preventiveActionsConsidered: nc.preventiveActionsConsidered || '',
    effectivenessReview: nc.effectivenessReview   || '',
    effectivenessDate:   nc.effectivenessDate     || '',
    verifiedBy:          nc.verifiedBy            || '',
    linkedRiskIds:       Array.isArray(nc.linkedRiskIds) ? nc.linkedRiskIds : [],
    linkedIncidentIds:   Array.isArray(nc.linkedIncidentIds) ? nc.linkedIncidentIds : [],
    norms:               Array.isArray(nc.norms) ? nc.norms : [],
    status:              nc.status               || 'Open',
    closedDate:          nc.closedDate            || '',
    createdAt:           nc.createdAt            || new Date().toISOString(),
  };
}

// ── SHARED STYLES ─────────────────────────────────────────────────────────────
const inputSt = (err) => ({
  ...FONT_LABEL,
  width: '100%', boxSizing: 'border-box',
  padding: '0.45rem 0.65rem',
  border: `1px solid ${err ? FIELD_ERROR_BORDER : BORDER_DEFAULT}`,
  borderRadius: 6,
  background: BG_SURFACE, color: TEXT_DEFAULT, outline: 'none',
});

const labelSt = {
  ...FONT_SMALL,
  color: TEXT_MUTED, letterSpacing: '0.03em',
  textTransform: 'uppercase', display: 'block', marginBottom: '0.3rem',
};

const sectionBarSt = {
  ...FONT_BODY_STRONG,
  color: TEXT_HEADING,
  marginBottom: '1rem', paddingBottom: '0.5rem',
  borderBottom: `1px solid ${BORDER_DEFAULT}`,
};

const thSt = {
  ...FONT_TINY,
  color: TEXT_MUTED, letterSpacing: '0.05em',
  textAlign: 'left', padding: '0.5rem 0.75rem',
  borderBottom: `1px solid ${STATUS_NEUTRAL.bg}`, whiteSpace: 'nowrap',
};

const tdSt = {
  ...FONT_LABEL,
  color: TEXT_DEFAULT, padding: '0.55rem 0.75rem',
  borderBottom: `1px solid ${BG_MUTED}`, verticalAlign: 'middle',
};

// List table style homologated with RiskList (used only for the outer NC list table)
const listThSt = {
  ...FONT_TINY,
  color: TEXT_MUTED,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  padding: '0.6rem 0.75rem',
  textAlign: 'left',
  borderBottom: `1px solid ${BORDER_DEFAULT}`,
  whiteSpace: 'nowrap',
  background: BG_SUBTLE,
};

const listTdSt = {
  ...FONT_LIST_ITEM,
  color: TEXT_DEFAULT,
  padding: '0.7rem 0.75rem',
  borderBottom: `1px solid ${BG_SHELL}`,
  verticalAlign: 'middle',
};

// ── SUB-COMPONENTS ────────────────────────────────────────────────────────────
function SummaryCard({ value, label, color }) {
  return (
    <div style={{
      background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`,
      borderRadius: 10, padding: '0.9rem 1.25rem',
      flex: 1, minWidth: 80,
    }}>
      <div style={{
        ...FONT_SMALL,
        color: TEXT_DEFAULT, letterSpacing: '0.04em',
        textTransform: 'uppercase', marginBottom: '0.35rem',
      }}>{label}</div>
      <div style={{
        ...FONT_STAT_VALUE, color: color || TEXT_HEADING,
      }}>{value}</div>
    </div>
  );
}

function NCStatusBadge({ status, lang }) {
  const sc = NC_STATUS_COLORS[status] || { bg: BG_MUTED, text: TEXT_MUTED };
  const label = (NC_STATUS_LABELS[lang] || NC_STATUS_LABELS.en)[status] || status;
  return (
    <span style={{
      ...FONT_SMALL,
      background: sc.bg, color: sc.text, borderRadius: 4,
      padding: '0.15rem 0.5rem',
      fontWeight: 600, whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

function CAStatusBadge({ status, lang }) {
  const sc = CA_STATUS_COLORS[status] || { bg: BG_MUTED, text: TEXT_MUTED };
  const label = (CA_STATUS_LABELS[lang] || CA_STATUS_LABELS.en)[status] || status;
  return (
    <span style={{
      ...FONT_TINY,
      background: sc.bg, color: sc.text, borderRadius: 4,
      padding: '0.12rem 0.4rem',
      fontWeight: 600, whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

// ── NC FORM ───────────────────────────────────────────────────────────────────
function NCForm({ t, lang, orgId, aiProcessingConsent, initialValues, onSave, onCancel, onDelete, saveRequestTick, saving, saveError, dirty, people }) {
  const [v, setV]         = useState(initialValues);
  const [errs, setErrs]   = useState({});
  const [rcInput, setRCI] = useState('');
  const [newCA, setNewCA] = useState({ action: '', responsible: [], deadline: '', status: 'Pending' });
  const [caErr, setCaErr] = useState(false);
  const [newCorr, setNewCorr] = useState({ description: '', responsible: '', completionDate: '' });
  const [aiSuggestingNC,  setAiSuggestingNC]  = useState(false);
  const [aiErrorNC,       setAiErrorNC]       = useState(null);
  const [aiSuggestingRCA, setAiSuggestingRCA] = useState(false);
  const [aiErrorRCA,      setAiErrorRCA]      = useState(null);
  // Audit list powers the auditId <select>. `null` = loading / load failed →
  // fall back to a plain text input so data entry is never blocked.
  const [auditsList,       setAuditsList]       = useState(null);
  const [riskOptions,      setRiskOptions]      = useState([]);
  const [incidentOptions,  setIncidentOptions]  = useState([]);
  const [confirmState, setConfirmState] = useState(null);

  useEffect(() => {
    let cancelled = false;
    listOrgCollection(orgId, 'audits')
      .then((items) => { if (!cancelled) setAuditsList(Array.isArray(items) ? items : []); })
      .catch(() => { if (!cancelled) setAuditsList(null); });
    return () => { cancelled = true; };
  }, [orgId]);

  useEffect(() => {
    let cancelled = false;
    listOrgCollection(orgId, 'risks')
      .then((items) => {
        if (!cancelled) setRiskOptions(items.map((r) => ({
          id: r.id,
          label: (r.ref ? r.ref + ' — ' : '') + (r.riskSummary || r.id),
        })));
      })
      .catch(() => { if (!cancelled) setRiskOptions([]); });
    return () => { cancelled = true; };
  }, [orgId]);

  useEffect(() => {
    let cancelled = false;
    listOrgCollection(orgId, 'incidents')
      .then((items) => {
        if (!cancelled) setIncidentOptions(items.map((i) => ({
          id: i.id,
          label: i.title || i.ref || i.id,
        })));
      })
      .catch(() => { if (!cancelled) setIncidentOptions([]); });
    return () => { cancelled = true; };
  }, [orgId]);

  useEffect(() => {
    if (saveRequestTick > 0) handleSubmit();
  }, [saveRequestTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clause items = ISO 27001 main clauses (4–10) only.
  // Stored value: bare clause ID (e.g. "9.2").
  const clauseItems = useMemo(() => (
    Object.entries(ISO_CLAUSES).map(([id, titles]) => ({
      id,
      label: `${id} — ${titles[lang] || titles.en}`,
    }))
  ), [lang]);

  // Control items = Annex A controls. Display format prepends "A." but the
  // stored ID remains bare (e.g. "5.1") for backward compatibility.
  const controlItems = useMemo(() => (
    SOA_CONTROLS.map((c) => ({
      id: c.id,
      label: `A.${c.id} — ${lang === 'es' ? c.title_es : c.title}`,
    }))
  ), [lang]);

  const set  = (f, val) => setV(p => ({ ...p, [f]: val }));
  const setNA = (f, val) => setNewCA(c => ({ ...c, [f]: val }));
  const setNC = (f, val) => setNewCorr(c => ({ ...c, [f]: val }));
  const srcL = SOURCE_LABELS[lang] || SOURCE_LABELS.en;
  const caSL = CA_STATUS_LABELS[lang] || CA_STATUS_LABELS.en;
  const fWrap = { display: 'flex', flexDirection: 'column', gap: '0.15rem' };
  const req   = <span style={{ color: ERROR_TEXT }}>{t.required}</span>;

  const handleAddRC = () => {
    if (!rcInput.trim()) return;
    set('rootCauses', [...v.rootCauses, rcInput.trim()]);
    setRCI('');
  };

  const handleAddCA = () => {
    if (!newCA.action.trim() || newCA.responsible.length === 0 || !newCA.deadline) {
      setCaErr(true); return;
    }
    setCaErr(false);
    const id = `ca-${String(v.correctiveActions.length + 1).padStart(3, '0')}`;
    set('correctiveActions', [
      ...v.correctiveActions,
      { id, action: newCA.action.trim(), responsible: newCA.responsible,
        deadline: newCA.deadline, status: newCA.status, evidenceId: null },
    ]);
    setNewCA({ action: '', responsible: [], deadline: '', status: 'Pending' });
  };

  const handleCAStatus = (idx, status) => {
    const updated = [...v.correctiveActions];
    updated[idx] = { ...updated[idx], status };
    set('correctiveActions', updated);
  };

  const handleDeleteCA = (idx) => {
    setConfirmState({
      message: t.caDelConfirm,
      confirmLabel: t.deleteBtn,
      onConfirm: () => {
        setConfirmState(null);
        set('correctiveActions', v.correctiveActions.filter((_, i) => i !== idx));
      },
    });
  };

  const updateActionField = (idx, field, val) => {
    const updated = v.correctiveActions.map((ca, i) =>
      i === idx ? { ...ca, [field]: val } : ca
    );
    set('correctiveActions', updated);
  };

  const handleSubmit = () => {
    const e = {};
    if (!v.ref.trim())            e.ref = true;
    if (!v.date)                  e.date = true;
    if (!v.owner.trim())          e.owner = true;
    if (!v.description.trim())    e.description = true;
    // At least one of clauseRefs or linkedControlIds must be present.
    if (v.clauseRefs.length === 0 && v.linkedControlIds.length === 0) {
      e.clauseOrControl = true;
    }
    // When closing, all corrective actions must be Completed.
    if (v.status === 'Closed') {
      const cas = Array.isArray(v.correctiveActions) ? v.correctiveActions : [];
      if (cas.length === 0 || !cas.every(ca => ca.status === 'Completed')) {
        e.caStatus = true;
      }
    }
    setErrs(e);
    if (Object.keys(e).length > 0) return;
    onSave(v);
  };

  const handleAISuggestNC = async () => {
    setAiSuggestingNC(true);
    setAiErrorNC(null);
    const prompt = `You are an ISO 27001:2022 nonconformity management expert.\nGiven the following nonconformity, suggest immediate correction actions.\nRespond ONLY with a valid JSON object, no markdown, no preamble.\nDescription: ${v.description}\nClause reference: ${v.clauseRefs.join(', ')}\nSource: ${v.source}\nReturn exactly this JSON:\n{\n"corrections": [{"description": "specific containment action in 1-2 sentences", "completionDate": null}]\n}`;
    const result = await callWorker({ prompt, maxTokens: 600 });
    if (!result.ok) {
      const busy = result.error === 'overloaded' || result.error === 'timeout';
      setAiErrorNC(busy ? t.aiErrorOverloaded : t.aiError);
      setAiSuggestingNC(false);
      return;
    }
    let parsed;
    try { parsed = JSON.parse(result.text); } catch {
      setAiErrorNC(t.aiError);
      setAiSuggestingNC(false);
      return;
    }
    if (Array.isArray(parsed.corrections) && v.corrections.length === 0) {
      const newCorrs = parsed.corrections.map((c, i) => ({
        id: `corr-${String(i + 1).padStart(3, '0')}`,
        description: typeof c.description === 'string' ? c.description : '',
        responsible: '',
        completionDate: c.completionDate || null,
      }));
      set('corrections', newCorrs);
    }
    setAiSuggestingNC(false);
  };

  const handleAISuggestRCA = async () => {
    setAiSuggestingRCA(true);
    setAiErrorRCA(null);
    const prompt = `You are an ISO 27001:2022 nonconformity management expert.\nGiven the following nonconformity, suggest root causes and corrective actions.\nRespond ONLY with a valid JSON object, no markdown, no preamble.\nDescription: ${v.description}\nClause reference: ${v.clauseRefs.join(', ')}\nCorrections taken: ${v.corrections.map(c => c.description).join('; ') || 'none'}\nReturn exactly this JSON:\n{\n"rootCauses": ["..."],\n"correctiveActions": ["..."]\n}\nEach array 2-3 specific items. correctiveActions items are plain action description strings, not objects.`;
    const result = await callWorker({ prompt, maxTokens: 600 });
    if (!result.ok) {
      const busy = result.error === 'overloaded' || result.error === 'timeout';
      setAiErrorRCA(busy ? t.aiErrorOverloaded : t.aiError);
      setAiSuggestingRCA(false);
      return;
    }
    let parsed;
    try { parsed = JSON.parse(result.text); } catch {
      setAiErrorRCA(t.aiError);
      setAiSuggestingRCA(false);
      return;
    }
    if (Array.isArray(parsed.rootCauses)) set('rootCauses', [...v.rootCauses, ...parsed.rootCauses]);
    if (Array.isArray(parsed.correctiveActions)) {
      const newCAs = parsed.correctiveActions.map((action, i) => ({
        id: `ca-${String(v.correctiveActions.length + i + 1).padStart(3, '0')}`,
        action: typeof action === 'string' ? action : String(action),
        responsible: [],
        deadline: '',
        status: 'Pending',
        evidenceId: null,
      }));
      set('correctiveActions', [...v.correctiveActions, ...newCAs]);
    }
    setAiSuggestingRCA(false);
  };

  const nfSt = (hasErr) => ({
    ...inputSt(hasErr), padding: '0.3rem 0.5rem', fontSize: '0.78rem',
  });

  return (
    <div style={{
      background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`,
      borderRadius: 12, padding: '1.75rem 2rem', marginTop: '0.5rem',
      position: 'relative',
    }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <button onClick={onCancel} style={{
          ...FONT_LABEL,
          background: 'none', border: 'none', cursor: 'pointer',
          color: BRAND_PRIMARY, padding: 0,
          display: 'block', marginBottom: '0.5rem',
        }}>{t.backToList}</button>
        <h3 style={{
          ...FONT_SECTION_TITLE,
          color: TEXT_HEADING, margin: 0,
        }}>
          {initialValues._isNew ? t.formTitleNew : t.formTitleEdit}
        </h3>
      </div>

      <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '0 0 2rem', letterSpacing: '0.04em' }}>{t.requiredLegend}</p>

      <fieldset disabled={saving} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>

      {/* ── Section A: General ──────────────────────────────────────────── */}
      <div style={sectionBarSt}>{t.sectionGeneral}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.9rem', marginBottom: '0.9rem' }}>
        <div style={fWrap}>
          <label style={labelSt}>{t.fRef} {req}</label>
          <input type="text" value={v.ref}
            onChange={e => set('ref', e.target.value)}
            style={inputSt(errs.ref)} />
        </div>
        <div style={fWrap}>
          <label style={labelSt}>{t.fDate} {req}</label>
          <input type="date" value={v.date}
            onChange={e => set('date', e.target.value)}
            style={inputSt(errs.date)} />
        </div>
        <div style={fWrap}>
          <label style={labelSt}>{t.fOwner} {req}</label>
          <PeopleSelector
            orgId={orgId}
            lang={lang}
            value={v.owner}
            onChange={(newVal) => set('owner', newVal)}
          />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.9rem', marginBottom: '0.9rem' }}>
        <div style={fWrap}>
          <label style={labelSt}>{t.fCategory} {req}</label>
          <select value={v.category} onChange={e => set('category', e.target.value)}
            style={inputSt(errs.category)}>
            {NC_CATEGORIES.map(c => (
              <option key={c} value={c}>{(NC_CATEGORY_LABELS[lang] || NC_CATEGORY_LABELS.en)[c] || c}</option>
            ))}
          </select>
        </div>
        <div style={fWrap}>
          <label style={labelSt}>{t.fSource}</label>
          <select value={v.source} onChange={e => set('source', e.target.value)}
            style={inputSt(false)}>
            {NC_SOURCES.map(s => (
              <option key={s} value={s}>{srcL[s] || s}</option>
            ))}
          </select>
        </div>
        <div style={{ ...fWrap, justifyContent: 'flex-end' }}>
          <label style={{ ...labelSt, marginBottom: '0.5rem' }}>&nbsp;</label>
          <label style={{ ...FONT_LABEL, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer',
            color: TEXT_DEFAULT }}>
            <input type="checkbox" checked={v.repeatFinding}
              onChange={e => set('repeatFinding', e.target.checked)} />
            {t.fRepeatFinding}
          </label>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.9rem', marginBottom: '1.4rem' }}>
        <div style={fWrap}>
          <label style={labelSt}>{t.fClause} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
          <EntitySelector
            items={clauseItems}
            selectedIds={v.clauseRefs}
            onAdd={(id) => set('clauseRefs', [...v.clauseRefs, id])}
            onRemove={(id) => set('clauseRefs', v.clauseRefs.filter(x => x !== id))}
            placeholder={t.fClausePh}
          />
        </div>
        <div style={fWrap}>
          <label style={labelSt}>{t.fControl}</label>
          <EntitySelector
            items={controlItems}
            selectedIds={v.linkedControlIds}
            onAdd={(id) => set('linkedControlIds', [...v.linkedControlIds, id])}
            onRemove={(id) => set('linkedControlIds', v.linkedControlIds.filter(x => x !== id))}
            placeholder={t.fControlPh}
          />
        </div>
      </div>
      {errs.clauseOrControl && (
        <div style={{
          ...FONT_LABEL,
          color: ERROR_TEXT, margin: '-0.75rem 0 1rem',
        }}>{t.clauseOrControlRequired}</div>
      )}
      <div style={{ ...fWrap, marginBottom: '1.4rem', maxWidth: 400 }}>
        <label style={labelSt}>{t.fAuditId}</label>
        {Array.isArray(auditsList) ? (
          <select
            value={v.auditId}
            onChange={e => set('auditId', e.target.value)}
            disabled={auditsList.length === 0}
            style={inputSt(false)}
          >
            <option value="">
              {auditsList.length === 0 ? t.noAudits : t.auditPickNone}
            </option>
            {auditsList.map(a => (
              <option key={a.id} value={a.id}>
                {a.ref} — {a.plannedDate || ''}
              </option>
            ))}
          </select>
        ) : (
          <input type="text" value={v.auditId}
            onChange={e => set('auditId', e.target.value)}
            placeholder="e.g. aud-20260101-001"
            style={inputSt(false)} />
        )}
      </div>

      {/* ── Section B: Description ──────────────────────────────────────── */}
      <div style={sectionBarSt}>{t.sectionDesc}</div>
      <div style={{ ...fWrap, marginBottom: '0.9rem' }}>
        <label style={labelSt}>{t.fDesc} {req}</label>
        <textarea rows={3} value={v.description}
          onChange={e => set('description', e.target.value)}
          style={{ ...inputSt(errs.description), resize: 'vertical' }} />
      </div>
      {/* Corrections sub-list */}
      <div style={{ marginBottom: '1.4rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
          <span style={labelSt}>{t.fCorrections}<span className="ai-spark">✦</span></span>
          <AISuggestButton onClick={handleAISuggestNC} loading={aiSuggestingNC} disabled={!v.description.trim() || !aiProcessingConsent} lang={lang} />
          {!aiProcessingConsent && <span style={{ ...FONT_SMALL, color: TEXT_MUTED }}>{t.aiConsentRequired}</span>}
        </div>
        {aiErrorNC && <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '0 0 0.5rem' }}>{aiErrorNC}</p>}
        {v.corrections.map((corr, idx) => (
          <div key={corr.id || idx} style={{
            display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: '0.5rem',
            alignItems: 'flex-start', marginBottom: '0.5rem',
            background: '#f9fafb', border: `1px solid ${STATUS_NEUTRAL.bg}`,
            borderRadius: 6, padding: '0.5rem 0.65rem',
          }}>
            <div>
              <div style={{ ...labelSt, fontSize: '0.65rem' }}>{t.fCorrectionDescription}</div>
              <div style={{ ...FONT_LABEL, color: TEXT_DEFAULT }}>{corr.description}</div>
            </div>
            <div>
              <div style={{ ...labelSt, fontSize: '0.65rem' }}>{t.fCorrectionResponsible}</div>
              <div style={{ ...FONT_LABEL, color: TEXT_DEFAULT }}>{resolvePerson(corr.responsible, people) || '—'}</div>
            </div>
            <div>
              <div style={{ ...labelSt, fontSize: '0.65rem' }}>{t.fCorrectionDate}</div>
              <div style={{ ...FONT_LABEL, color: TEXT_DEFAULT }}>{corr.completionDate || '—'}</div>
            </div>
            <button type="button"
              onClick={() => set('corrections', v.corrections.filter((_, i) => i !== idx))}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: TEXT_DISABLED, fontSize: '0.9rem', paddingTop: '1.4rem' }}>
              ✕
            </button>
          </div>
        ))}
        {/* Add correction entry */}
        <div style={{
          display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: '0.5rem',
          alignItems: 'flex-start', marginTop: '0.5rem',
          background: BG_SURFACE, border: `1px dashed ${BORDER_DEFAULT}`,
          borderRadius: 6, padding: '0.5rem 0.65rem',
        }}>
          <div>
            <div style={{ ...labelSt, fontSize: '0.65rem' }}>{t.fCorrectionDescription}</div>
            <input type="text" value={newCorr.description}
              onChange={e => setNC('description', e.target.value)}
              style={{ ...inputSt(false), padding: '0.3rem 0.5rem', fontSize: '0.78rem' }} />
          </div>
          <div>
            <div style={{ ...labelSt, fontSize: '0.65rem' }}>{t.fCorrectionResponsible}</div>
            <PeopleSelector orgId={orgId} lang={lang} value={newCorr.responsible}
              onChange={val => setNC('responsible', val)} />
          </div>
          <div>
            <div style={{ ...labelSt, fontSize: '0.65rem' }}>{t.fCorrectionDate}</div>
            <input type="date" value={newCorr.completionDate}
              onChange={e => setNC('completionDate', e.target.value)}
              style={{ ...inputSt(false), padding: '0.3rem 0.5rem', fontSize: '0.78rem' }} />
          </div>
          <button type="button"
            onClick={() => {
              if (!newCorr.description.trim()) return;
              const id = `corr-${String(v.corrections.length + 1).padStart(3, '0')}`;
              set('corrections', [...v.corrections, { id, ...newCorr }]);
              setNewCorr({ description: '', responsible: '', completionDate: '' });
            }}
            style={{
              ...FONT_SMALL,
              background: TEXT_HEADING, color: SURFACE_WHITE, border: 'none',
              borderRadius: 6, padding: '0.3rem 0.6rem', cursor: 'pointer',
              marginTop: '1.4rem', whiteSpace: 'nowrap',
            }}>
            {t.caAddBtn}
          </button>
        </div>
      </div>

      {/* ── Section C: Root causes ──────────────────────────────────────── */}
      <div style={{ ...sectionBarSt, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '1rem' }}>
        <span>{t.sectionRC}<span className="ai-spark">✦</span></span>
        <AISuggestButton onClick={handleAISuggestRCA} loading={aiSuggestingRCA} disabled={!v.description.trim() || !aiProcessingConsent} lang={lang} />
        {!aiProcessingConsent && <span style={{ ...FONT_SMALL, color: TEXT_MUTED }}>{t.aiConsentRequired}</span>}
      </div>
      {aiErrorRCA && <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '-0.5rem 0 1rem' }}>{aiErrorRCA}</p>}
      <div style={{ marginBottom: '1.4rem' }}>
        {v.rootCauses.length === 0 && (
          <p style={{ ...FONT_LABEL,
            color: TEXT_DISABLED, margin: '0 0 0.6rem' }}>{t.noRC}</p>
        )}
        {v.rootCauses.map((rc, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'flex-start',
            gap: '0.5rem', marginBottom: '0.35rem' }}>
            <span style={{
              ...FONT_LABEL,
              color: TEXT_DEFAULT, flex: 1,
              background: BG_MUTED, borderRadius: 5,
              padding: '0.35rem 0.6rem', lineHeight: 1.4,
            }}>{rc}</span>
            <button type="button"
              onClick={() => set('rootCauses', v.rootCauses.filter((_, i) => i !== idx))}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: TEXT_DISABLED, fontSize: '0.9rem', lineHeight: 1,
                padding: '0.35rem 0', flexShrink: 0,
              }}>✕</button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
          <input type="text" value={rcInput}
            onChange={e => setRCI(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddRC(); } }}
            placeholder={t.fRCPh}
            style={{ ...inputSt(false), flex: 1 }} />
          <button type="button" onClick={handleAddRC}
            style={{
              ...BTN_PRIMARY, ...FONT_LABEL, border: 'none',
              borderRadius: 6, padding: '0 0.9rem', cursor: 'pointer',
              flexShrink: 0,
            }}>+</button>
        </div>
      </div>

      {/* ── Section D: Corrective actions ───────────────────────────────── */}
      <div style={sectionBarSt}>{t.sectionCAs}<span className="ai-spark">✦</span></div>
      <div style={{ overflowX: 'auto', marginBottom: '1.4rem' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: 560 }}>
          <colgroup>
            <col style={{ width: '32%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '5%' }} />
          </colgroup>
          <thead>
            <tr>
              {[t.caColAction, t.caColResp, t.caColDeadline, t.caColStatus, ''].map((col, i) => (
                <th key={i} style={thSt}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {v.correctiveActions.map((ca, idx) => {
              const sc = CA_STATUS_COLORS[ca.status] || CA_STATUS_COLORS['Pending'];
              return (
                <tr key={idx} style={{ borderBottom: `1px solid ${BG_MUTED}` }}>
                  <td style={tdSt}>{ca.action}</td>
                  <td style={{ ...tdSt, verticalAlign: 'top' }}>
                    <PeopleSelector
                      orgId={orgId}
                      lang={lang}
                      multiple
                      value={Array.isArray(ca.responsible) ? ca.responsible : (ca.responsible ? [ca.responsible] : [])}
                      onChange={(val) => updateActionField(idx, 'responsible', val)}
                    />
                  </td>
                  <td style={tdSt}>{ca.deadline}</td>
                  <td style={{ ...tdSt }}>
                    <select value={ca.status}
                      onChange={e => handleCAStatus(idx, e.target.value)}
                      style={{
                        ...FONT_SMALL,
                        background: sc.bg, color: sc.text,
                        border: 'none', borderRadius: 4,
                        fontWeight: 600, padding: '0.2rem 0.35rem',
                        cursor: 'pointer', outline: 'none', width: '100%',
                      }}>
                      {CA_STATUSES.map(s => (
                        <option key={s} value={s}>{caSL[s] || s}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ ...tdSt, textAlign: 'center' }}>
                    <button type="button" onClick={() => handleDeleteCA(idx)}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: TEXT_DISABLED, fontSize: '0.9rem', lineHeight: 1, padding: 0,
                      }}>✕</button>
                  </td>
                </tr>
              );
            })}
            {/* Add-entry row */}
            <tr style={{ background: BG_SURFACE, borderTop: `2px solid ${STATUS_NEUTRAL.bg}` }}>
              <td style={{ padding: '0.4rem 0.5rem' }}>
                <input type="text" value={newCA.action}
                  onChange={e => setNA('action', e.target.value)}
                  placeholder={t.caFAction}
                  style={nfSt(caErr && !newCA.action.trim())} />
              </td>
              <td style={{ padding: '0.4rem 0.5rem', verticalAlign: 'top' }}>
                <PeopleSelector
                  orgId={orgId}
                  lang={lang}
                  multiple
                  value={newCA.responsible}
                  onChange={(val) => setNA('responsible', val)}
                  placeholder={t.caFResp}
                />
              </td>
              <td style={{ padding: '0.4rem 0.5rem' }}>
                <input type="date" value={newCA.deadline}
                  onChange={e => setNA('deadline', e.target.value)}
                  style={nfSt(caErr && !newCA.deadline)} />
              </td>
              <td style={{ padding: '0.4rem 0.5rem' }}>
                <select value={newCA.status}
                  onChange={e => setNA('status', e.target.value)}
                  style={{ ...nfSt(false), width: '100%' }}>
                  {CA_STATUSES.map(s => (
                    <option key={s} value={s}>{caSL[s] || s}</option>
                  ))}
                </select>
              </td>
              <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                <button type="button" onClick={handleAddCA}
                  style={{
                    ...FONT_SMALL,
                    background: TEXT_HEADING, color: SURFACE_WHITE, border: 'none',
                    borderRadius: 6, padding: '0.3rem 0.5rem', cursor: 'pointer',
                  }}>{t.caAddBtn}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── Section D2: Relationships ───────────────────────────────────── */}
      <div style={sectionBarSt}>{t.sectionRel}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.9rem', marginBottom: '0.9rem' }}>
        <div style={fWrap}>
          <label style={labelSt}>{t.fParentNC}</label>
          <input type="text" value={v.parentNonconformityId || ''}
            onChange={e => set('parentNonconformityId', e.target.value || null)}
            placeholder={t.fParentNCPh}
            style={inputSt(false)} />
        </div>
        <div style={fWrap}>
          <label style={labelSt}>{t.fContainment}</label>
          <input type="date" value={v.containmentDate}
            onChange={e => set('containmentDate', e.target.value)}
            style={inputSt(false)} />
        </div>
      </div>
      <div style={{ ...fWrap, marginBottom: '0.9rem' }}>
        <label style={labelSt}>{t.fPreventive}</label>
        <textarea rows={3} value={v.preventiveActionsConsidered}
          onChange={e => set('preventiveActionsConsidered', e.target.value)}
          style={{ ...inputSt(false), resize: 'vertical' }} />
      </div>
      <div style={{ ...fWrap, marginBottom: '0.9rem' }}>
        <label style={labelSt}>{t.fLinkedRisks}</label>
        <EntitySelector
          items={riskOptions}
          selectedIds={v.linkedRiskIds}
          onAdd={(id) => set('linkedRiskIds', [...v.linkedRiskIds, id])}
          onRemove={(id) => set('linkedRiskIds', v.linkedRiskIds.filter(x => x !== id))}
          placeholder={t.riskPlaceholder}
        />
      </div>
      <div style={{ ...fWrap, marginBottom: '1.4rem' }}>
        <label style={labelSt}>{t.fLinkedIncidents}</label>
        {incidentOptions.length > 0 ? (
          <EntitySelector
            items={incidentOptions}
            selectedIds={v.linkedIncidentIds}
            onAdd={(id) => set('linkedIncidentIds', [...v.linkedIncidentIds, id])}
            onRemove={(id) => set('linkedIncidentIds', v.linkedIncidentIds.filter(x => x !== id))}
            placeholder={t.incidentPlaceholder}
          />
        ) : (
          <TagInput
            tags={v.linkedIncidentIds}
            onAdd={(val) => set('linkedIncidentIds', [...v.linkedIncidentIds, val])}
            onRemove={(val) => set('linkedIncidentIds', v.linkedIncidentIds.filter(x => x !== val))}
            onUpdate={(oldValue, newValue) => set('linkedIncidentIds', v.linkedIncidentIds.map((x) => x === oldValue ? newValue : x))}
            placeholder={t.incidentPlaceholder}
          />
        )}
      </div>

      {/* ── Section E: Effectiveness ─────────────────────────────────────── */}
      <div style={sectionBarSt}>{t.sectionEffect}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.9rem', marginBottom: '1.4rem' }}>
        <div style={fWrap}>
          <label style={labelSt}>{t.fEffectReview}</label>
          <textarea rows={3} value={v.effectivenessReview}
            onChange={e => set('effectivenessReview', e.target.value)}
            style={{ ...inputSt(false), resize: 'vertical' }} />
        </div>
        <div style={fWrap}>
          <label style={labelSt}>{t.fEffectDate}</label>
          <input type="date" value={v.effectivenessDate}
            onChange={e => set('effectivenessDate', e.target.value)}
            style={inputSt(false)} />
        </div>
      </div>

      {/* ── Section F: Status ────────────────────────────────────────────── */}
      <div style={sectionBarSt}>{t.sectionSt}</div>
      <div style={{ display: 'grid', gridTemplateColumns: v.status === 'Closed' ? '1fr 1fr 1fr' : '1fr', gap: '0.9rem', marginBottom: '1.5rem', maxWidth: 720 }}>
        <div style={fWrap}>
          <label style={labelSt}>{t.fStatus} {req}</label>
          <select value={v.status} onChange={e => set('status', e.target.value)}
            style={inputSt(false)}>
            {NC_STATUSES.map(s => (
              <option key={s} value={s}>{(NC_STATUS_LABELS[lang] || NC_STATUS_LABELS.en)[s] || s}</option>
            ))}
          </select>
        </div>
        {v.status === 'Closed' && (
          <div style={fWrap}>
            <label style={labelSt}>{t.fClosedDate}</label>
            <input type="date" value={v.closedDate}
              onChange={e => set('closedDate', e.target.value)}
              style={inputSt(false)} />
          </div>
        )}
        {v.status === 'Closed' && (
          <div style={fWrap}>
            <label style={labelSt}>{t.fVerifiedBy}</label>
            <PeopleSelector orgId={orgId} lang={lang} value={v.verifiedBy}
              onChange={val => set('verifiedBy', val)} />
          </div>
        )}
      </div>

      {/* Sticky action bar */}
      <div style={{
        position: 'sticky',
        bottom: 0,
        marginLeft: '-2rem',
        marginRight: '-2rem',
        marginBottom: '-1.75rem',
        marginTop: '1.5rem',
        padding: '0.85rem 2rem',
        background: BG_SURFACE,
        borderTop: `1px solid ${BORDER_DEFAULT}`,
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12,
        zIndex: 10,
      }}>
        {Object.keys(errs).length > 0 && (
          <div style={{
            ...FONT_LABEL,
            color: ERROR_TEXT, marginBottom: '0.5rem',
          }}>{t.validationError}</div>
        )}
        {errs.caStatus && (
          <div style={{
            ...FONT_LABEL,
            color: ERROR_TEXT, marginBottom: '0.5rem',
          }}>{t.closeError}</div>
        )}
        {saveError && (
          <div style={{
            ...FONT_LABEL,
            background: ERROR_BG, color: ERROR_TEXT, border: `1px solid ${ERROR_BORDER}`,
            borderRadius: 6, padding: '0.35rem 0.75rem',
            marginBottom: '0.5rem',
          }}>{t.saveError}</div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div>
            {!initialValues._isNew && onDelete && (
              <button
                type="button"
                onClick={onDelete}
                disabled={saving}
                style={{
                  ...BTN_DESTRUCTIVE_OUTLINE,
                  ...FONT_LABEL,
                  borderRadius: 8,
                  padding: '0.5rem 1rem',
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >{t.deleteFormBtn}</button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {dirty && (
              <span style={{
                ...FONT_LABEL,
                color: UNSAVED_AMBER,
              }}>{t.unsavedLabel}</span>
            )}
            <button type="button" onClick={onCancel} disabled={saving} style={{
              ...BTN_SECONDARY, ...FONT_BUTTON, fontWeight: 400, borderRadius: 8,
              padding: '0.6rem 1.25rem', cursor: saving ? 'not-allowed' : 'pointer',
            }}>{t.formCancel}</button>
            <button type="button" onClick={handleSubmit} disabled={saving} style={{
              ...BTN_PRIMARY, ...FONT_BUTTON, fontWeight: 700,
              background: saving ? TEXT_MUTED : BTN_PRIMARY.background, border: 'none',
              borderRadius: 8, padding: '0.6rem 1.6rem', cursor: saving ? 'not-allowed' : 'pointer',
            }}>{saving ? t.saving : t.formSave}</button>
          </div>
        </div>
      </div>
      </fieldset>
      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={t.deleteFormBtn || 'Delete'}
        cancelLabel={t.formCancel || 'Cancel'}
      />
    </div>
  );
}

// ── NC DETAIL VIEW ────────────────────────────────────────────────────────────
function NCDetailView({ t, lang, nc, onEdit, onBack, onClose, people }) {
  const [closeError, setCloseError] = useState(false);
  const [closing,    setClosing]    = useState(false);
  const srcL = SOURCE_LABELS[lang] || SOURCE_LABELS.en;
  // §11 rule 34 — read-only banner shown when the NC is in a terminal/
  // immutable status (Closed). Closed NCs hide the Edit button entirely so
  // the user does not hit the circular handleEdit redirect.
  const frozen = isRecordImmutable(nc, 'nonconformity');

  const handleClose = async () => {
    const cas = Array.isArray(nc.correctiveActions) ? nc.correctiveActions : [];
    if (cas.length === 0 || !cas.every(ca => ca.status === 'Completed')) {
      setCloseError(true);
      return;
    }
    setCloseError(false);
    setClosing(true);
    try {
      await onClose(nc);
    } finally {
      setClosing(false);
    }
  };

  const dl = { ...FONT_LABEL,
    color: TEXT_MUTED, letterSpacing: '0.03em', textTransform: 'uppercase',
    marginBottom: '0.2rem' };
  const dd = { ...FONT_LABEL,
    color: TEXT_DEFAULT, marginBottom: '0.9rem', lineHeight: 1.5 };

  return (
    <div style={{
      background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`,
      borderRadius: 12, padding: '1.75rem 2rem', marginTop: '0.5rem',
    }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <button onClick={onBack} style={{
          ...FONT_LABEL,
          background: 'none', border: 'none', cursor: 'pointer',
          color: TEXT_MUTED, padding: 0,
          textDecoration: 'underline', textDecorationColor: BORDER_DEFAULT,
          display: 'block', marginBottom: '0.5rem',
        }}>{t.backToList}</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem',
          flexWrap: 'wrap' }}>
          <span style={{
            ...FONT_LABEL, fontWeight: 600,
            color: TEXT_HEADING,
          }}>{nc.ref}</span>
          <NCStatusBadge status={nc.status} lang={lang} />
          {nc.auditId && (
            <span style={{
              ...FONT_SMALL,
              background: '#e0e7ef', color: TEXT_DEFAULT, borderRadius: 4,
              padding: '0.15rem 0.5rem',
              fontWeight: 500,
            }}>{t.dAuditRef} {nc.auditId}</span>
          )}
        </div>
      </div>

      {frozen && (
        <div style={{
          background: STATUS_NEUTRAL.bg, border: `1px solid ${BORDER_DEFAULT}`,
          borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem',
          ...FONT_LABEL, color: TEXT_DEFAULT,
        }}>
          {t.frozenBanner}
        </div>
      )}

      {/* General info grid */}
      <div style={sectionBarSt}>{t.sectionGeneral}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem 1.5rem', marginBottom: '1.4rem' }}>
        {[
          [t.fDate,   nc.date    || '—'],
          [t.fOwner,  resolvePerson(nc.owner, people) || '—'],
          [t.fSource, srcL[nc.source] || nc.source || '—'],
          [t.fClause, (Array.isArray(nc.clauseRefs) ? nc.clauseRefs : []).join(', ') || '—'],
          [t.fControl, (Array.isArray(nc.linkedControlIds) ? nc.linkedControlIds.map(id => `A.${id}`) : []).join(', ') || '—'],
          [t.fCategory, (NC_CATEGORY_LABELS[lang] || NC_CATEGORY_LABELS.en)[nc.category] || nc.category || '—'],
        ].map(([label, val]) => (
          <div key={label}>
            <div style={dl}>{label}</div>
            <div style={dd}>{val}</div>
          </div>
        ))}
      </div>

      {/* Description */}
      <div style={sectionBarSt}>{t.sectionDesc}</div>
      <div style={{ marginBottom: '1.4rem' }}>
        <div style={{ ...dd, marginBottom: '0.75rem' }}>{nc.description || '—'}</div>
        {Array.isArray(nc.corrections) && nc.corrections.length > 0 && (
          <>
            <div style={dl}>{t.dImmediate}</div>
            {nc.corrections.map((corr, i) => (
              <div key={corr.id || i} style={{
                ...FONT_LABEL,
                color: TEXT_DEFAULT, background: BG_MUTED, borderRadius: 5,
                padding: '0.35rem 0.6rem', marginBottom: '0.3rem', lineHeight: 1.4,
              }}>
                {corr.description}
                {corr.completionDate && (
                  <span style={{ marginLeft: '0.75rem', color: TEXT_DISABLED, fontSize: '0.75rem' }}>
                    {corr.completionDate}
                  </span>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      {/* Root causes */}
      <div style={sectionBarSt}>{t.dRC}</div>
      <div style={{ marginBottom: '1.4rem' }}>
        {(!nc.rootCauses || nc.rootCauses.length === 0) ? (
          <p style={{ ...FONT_LABEL, color: TEXT_DISABLED, margin: 0 }}>{t.noRC}</p>
        ) : (
          <ul style={{ margin: 0, padding: '0 0 0 1.2rem' }}>
            {nc.rootCauses.map((rc, i) => (
              <li key={i} style={{ ...FONT_LABEL,
                color: TEXT_DEFAULT, marginBottom: '0.25rem', lineHeight: 1.5 }}>{rc}</li>
            ))}
          </ul>
        )}
      </div>

      {/* Corrective actions */}
      <div style={sectionBarSt}>{t.dCAs}</div>
      <div style={{ overflowX: 'auto', marginBottom: '1.4rem' }}>
        {(!nc.correctiveActions || nc.correctiveActions.length === 0) ? (
          <p style={{ ...FONT_LABEL, color: TEXT_DISABLED, margin: 0 }}>{t.noCAs}</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {[t.caColAction, t.caColResp, t.caColDeadline, t.caColStatus].map(col => (
                  <th key={col} style={thSt}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {nc.correctiveActions.map((ca, i) => (
                <tr key={i}>
                  <td style={tdSt}>{ca.action}</td>
                  <td style={tdSt}>{Array.isArray(ca.responsible) ? ca.responsible.map(id => resolvePerson(id, people)).join(', ') : resolvePerson(ca.responsible, people) || '—'}</td>
                  <td style={tdSt}>{ca.deadline}</td>
                  <td style={tdSt}><CAStatusBadge status={ca.status} lang={lang} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Effectiveness */}
      {(nc.effectivenessReview || nc.effectivenessDate) && (
        <>
          <div style={sectionBarSt}>{t.dEffect}</div>
          <div style={{ marginBottom: '1.4rem' }}>
            {nc.effectivenessReview && <div style={dd}>{nc.effectivenessReview}</div>}
            {nc.effectivenessDate && (
              <div>
                <span style={dl}>{t.dEffectDate} </span>
                <span style={{ ...FONT_LABEL, color: TEXT_DEFAULT }}>
                  {nc.effectivenessDate}
                </span>
              </div>
            )}
          </div>
        </>
      )}

      {/* Closed date */}
      {nc.closedDate && (
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ ...FONT_SMALL,
            color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
            {t.dClosedDate}{' '}
          </span>
          <span style={{ ...FONT_LABEL, color: TEXT_DEFAULT }}>
            {nc.closedDate}
          </span>
        </div>
      )}

      {/* Close error */}
      {closeError && (
        <div style={{
          ...FONT_LABEL,
          color: ERROR_TEXT, marginBottom: '0.75rem',
        }}>{t.closeError}</div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        {!frozen && (
          <button onClick={() => onEdit(nc)} style={{
            ...BTN_PRIMARY, ...FONT_BUTTON, border: 'none',
            borderRadius: 8, padding: '0.5rem 1.1rem', cursor: 'pointer',
          }}>{t.editBtn}</button>
        )}
        {nc.status !== 'Closed' && (
          <button
            onClick={handleClose}
            disabled={closing}
            style={{
              ...BTN_STATUS_FORWARD, ...FONT_BUTTON,
              borderRadius: 8, padding: '0.5rem 1.1rem', cursor: closing ? 'default' : 'pointer',
              opacity: closing ? 0.6 : 1,
            }}
          >{closing ? t.closingBtn : t.closeBtn}</button>
        )}
      </div>
    </div>
  );
}

// ── MULTI-SELECT FILTER DROPDOWN ──────────────────────────────────────────────
// Self-contained dropdown with checkboxes. Stays open while selecting; closes
// on outside click. Shows "{n} selected" summary or the "all" label when empty.
function MultiSelectFilter({ options, values, onChange, allLabel, nSelectedTpl, getLabel }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (v) => {
    if (values.includes(v)) onChange(values.filter(x => x !== v));
    else                    onChange([...values, v]);
  };

  const summary = values.length === 0
    ? allLabel
    : nSelectedTpl.replace('{n}', values.length);

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          ...FONT_LABEL,
          background: BG_SURFACE,
          border: `1px solid ${BORDER_DEFAULT}`,
          borderRadius: 6,
          padding: '0.3rem 0.5rem',
          color: TEXT_DEFAULT,
          cursor: 'pointer',
          textAlign: 'left',
          minWidth: 140,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.5rem',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</span>
        <span style={{ color: TEXT_DISABLED, fontSize: '0.7rem' }}>▾</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0,
          marginTop: 2, zIndex: 200,
          background: SURFACE_WHITE, border: `1px solid ${BORDER_DEFAULT}`,
          borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          minWidth: 220, maxHeight: 260, overflowY: 'auto',
          padding: '0.3rem 0',
        }}>
          {options.length === 0 && (
            <div style={{
              ...FONT_LABEL,
              padding: '0.4rem 0.65rem',
              color: TEXT_DISABLED,
            }}>—</div>
          )}
          {options.map(opt => {
            const checked = values.includes(opt);
            return (
              <label
                key={opt}
                style={{
                  ...FONT_LABEL,
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.35rem 0.65rem',
                  color: TEXT_DEFAULT, cursor: 'pointer',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = BG_CONTENT; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(opt)}
                />
                <span>{getLabel ? getLabel(opt) : opt}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── NONCONFORMITIES VIEW ──────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
export default function NonconformitiesView({ org, lang, onBack, onNavigate, onDirtyChange, saveRequestTick: appSaveRequestTick, onSaveResult, initialPrefill = null }) {
  const t = T[lang] || T.en;

  const [allNCs,      setAllNCs]      = useState(null);   // null = loading
  const [view,        setView]        = useState('list'); // 'list' | 'form' | 'detail'
  const [selectedNc,  setSelectedNc]  = useState(null);
  const [formInitial, setFormInitial] = useState(null);
  const [saveState,   setSaveState]   = useState('idle'); // 'idle'|'error'
  const [saving,      setSaving]      = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterOwner,  setFilterOwner]  = useState('');
  const [filterClauseControl, setFilterClauseControl] = useState([]); // multi-select
  const [selectedIds,  setSelectedIds]  = useState(new Set());
  const [confirmState, setConfirmState] = useState(null);
  const [scanningDelete, setScanningDelete] = useState(false);
  const [dirty, setDirty]               = useState(false);
  const [saveRequestTick, setSaveRequestTick] = useState(0);
  const pendingToListRef = useRef(false);
  // App-level "Save and leave" — flagged when the App tick increases so
  // the next form save reports back via onSaveResult instead of running
  // the form-Cancel close-form path. See GRC_UI_PATTERNS.md §5.
  const pendingAppSaveRef               = useRef(false);
  const lastProcessedAppTickRef         = useRef(0);
  const lastProcessedPrefillRef         = useRef(null);
  const [people, setPeople] = useState([]);

  // ── Load ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    listOrgCollection(org.id, 'nonconformities')
      .then(items => setAllNCs(items))
      .catch(() => setAllNCs([]));
  }, [org.id]);

  useEffect(() => {
    readOrgFile(org.id, 'people.json')
      .then(data => setPeople(Array.isArray(data.people) ? data.people : []))
      .catch(() => setPeople([]));
  }, [org.id]);

  // ── Consume cross-module prefill (e.g. Awareness → NC) once per arrival ──
  useEffect(() => {
    if (!initialPrefill) return;
    if (lastProcessedPrefillRef.current === initialPrefill) return;
    lastProcessedPrefillRef.current = initialPrefill;
    let cancelled = false;
    (async () => {
      const ncPrefix = (org.refPrefixes && org.refPrefixes.nonconformity) || 'nco';
      let seq;
      try {
        const items = await listOrgCollection(org.id, 'nonconformities');
        const year = new Date().getFullYear();
        const prefix = `${ncPrefix}-${year}-`;
        let max = 0;
        items.forEach(item => {
          if (item.ref && item.ref.startsWith(prefix)) {
            const num = parseInt(item.ref.slice(prefix.length), 10);
            if (!isNaN(num) && num > max) max = num;
          }
        });
        seq = max + 1;
      } catch {
        const ts = Date.now().toString().slice(-4);
        seq = parseInt(ts, 10);
      }
      if (cancelled) return;
      const base = blankForm(seq, ncPrefix);
      const prefilledForm = {
        ...base,
        ...(typeof initialPrefill.source === 'string' && initialPrefill.source
          ? { source: initialPrefill.source } : {}),
        ...(typeof initialPrefill.description === 'string' && initialPrefill.description
          ? { description: initialPrefill.description } : {}),
        _isNew: true,
      };
      setFormInitial(prefilledForm);
      setSelectedNc(null);
      setView('form');
    })();
    return () => { cancelled = true; };
  }, [initialPrefill]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Propagate dirty state to App.js navigation guard ─────────────────────
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  // ── App-level guard: bridge App tick into local tick to fire the embedded
  //    form's existing saveRequestTick handler. See GRC_UI_PATTERNS.md §5.
  useEffect(() => {
    if (typeof appSaveRequestTick !== 'number') return;
    if (appSaveRequestTick <= lastProcessedAppTickRef.current) return;
    lastProcessedAppTickRef.current = appSaveRequestTick;
    if (view === 'form' && formInitial !== null) {
      pendingAppSaveRef.current = true;
      setSaveRequestTick(n => n + 1);
    } else {
      onSaveResult?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSaveRequestTick]);

  // ── Form cancel ───────────────────────────────────────────────────────────
  const handleFormCancel = () => {
    if (dirty) {
      setConfirmState({
        message: t.unsavedConfirm,
        confirmLabel: t.leaveWithoutSaving,
        onConfirm: () => {
          setConfirmState(null);
          setDirty(false);
          setView('list');
          setFormInitial(null);
          setSelectedNc(null);
        },
        secondaryAction: {
          label: t.saveAndLeave,
          onClick: () => {
            setConfirmState(null);
            pendingToListRef.current = true;
            setSaveRequestTick(n => n + 1);
          },
        },
      });
      return;
    }
    setDirty(false);
    setView('list');
    setFormInitial(null);
    setSelectedNc(null);
  };

  // ── New ───────────────────────────────────────────────────────────────────
  const handleNew = async () => {
    const ncPrefix = (org.refPrefixes && org.refPrefixes.nonconformity) || 'nco';
    let seq;
    try {
      const items = await listOrgCollection(org.id, 'nonconformities');
      const year = new Date().getFullYear();
      const prefix = `${ncPrefix}-${year}-`;
      let max = 0;
      items.forEach(item => {
        if (item.ref && item.ref.startsWith(prefix)) {
          const num = parseInt(item.ref.slice(prefix.length), 10);
          if (!isNaN(num) && num > max) max = num;
        }
      });
      seq = max + 1;
    } catch {
      const ts = Date.now().toString().slice(-4);
      seq = parseInt(ts, 10);
    }
    setFormInitial({ ...blankForm(seq, ncPrefix), _isNew: true });
    setSelectedNc(null);
    setView('form');
  };

  // ── Edit ──────────────────────────────────────────────────────────────────
  const handleEdit = (nc) => {
    // §11 rule 34 — a Closed NC cannot be edited directly. Route to the
    // read-only detail view; reopening is a Settings-only action.
    if (isRecordImmutable(nc, 'nonconformity')) {
      setSelectedNc(nc);
      setView('detail');
      setDirty(false);
      return;
    }
    setFormInitial({ ...ncToForm(nc), _isNew: false });
    setSelectedNc(nc);
    setView('form');
    setDirty(false);
  };

  // ── View detail ───────────────────────────────────────────────────────────
  const handleView = (nc) => {
    setSelectedNc(nc);
    setView('detail');
  };

  // ── §11 rule 42 helpers ─────────────────────────────────────────────────
  const renderBlockContent = (hits) => (
    <div>
      <h3 style={{ ...FONT_BODY, fontWeight: 700, color: TEXT_HEADING, margin: '0 0 0.5rem' }}>
        {t.deleteBlockedTitle}
      </h3>
      <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: '0 0 0.75rem' }}>
        {t.deleteBlockedReferencedBy} {summariseHits(hits, t)}
      </p>
      <div style={{ maxHeight: '40vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1.25rem' }}>
        {hits.map((h, i) => (
          <button
            key={i}
            type="button"
            onClick={() => { setConfirmState(null); onNavigate?.(HOLDER_NAV[h.holderType], null); }}
            style={{ ...BTN_SECONDARY, ...FONT_LABEL, borderRadius: 6, padding: '0.35rem 0.6rem', textAlign: 'left', cursor: 'pointer' }}
          >
            {`${holderLabel(t, h.holderType, 1)} ${h.holderRef}`}
          </button>
        ))}
      </div>
    </div>
  );

  const renderBulkContent = (allBlocked, deletableCount, blockedCount, total, blockedSummaries) => (
    <div>
      <h3 style={{ ...FONT_BODY, fontWeight: 700, color: TEXT_HEADING, margin: '0 0 0.5rem' }}>
        {t.deleteBlockedTitle}
      </h3>
      <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: '0 0 0.75rem' }}>
        {allBlocked
          ? t.bulkDeleteSummary_allBlocked
          : t.bulkDeleteSummary_mixed
              .replace('{deletable}', String(deletableCount))
              .replace('{blocked}',   String(blockedCount))
              .replace('{total}',     String(total))}
      </p>
      <div style={{ maxHeight: '40vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1.25rem' }}>
        {blockedSummaries.map((row, i) => (
          <div key={i} style={{ ...FONT_LABEL, color: TEXT_DEFAULT, padding: '0.3rem 0.5rem', border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, background: BG_SURFACE }}>
            <span style={{ fontWeight: 600 }}>{row.label}</span>
            <span style={{ color: TEXT_MUTED }}>{` — ${t.deleteBlockedReferencedBy} ${row.summary}`}</span>
          </div>
        ))}
      </div>
    </div>
  );

  // ── Delete from form (edit mode) ─────────────────────────────────────────
  const handleDeleteFromForm = async () => {
    if (!selectedNc) return;
    const nc = selectedNc;
    if (scanningDelete) return;
    setScanningDelete(true);
    let hits = [];
    try { hits = await scanReferencesToEntity(org.id, 'nonconformity', nc.id); }
    catch { hits = []; }
    setScanningDelete(false);
    if (hits.length === 0) {
      setConfirmState({
        message: t.deleteConfirm,
        confirmLabel: t.deleteBtn,
        onConfirm: async () => {
          setConfirmState(null);
          try {
            await deleteOrgFile(org.id, `nonconformities/${nc.id}.json`);
            setAllNCs(prev => (prev || []).filter(n => n.id !== nc.id));
            setSelectedIds(prev => { const next = new Set(prev); next.delete(nc.id); return next; });
            setView('list');
            setFormInitial(null);
            setSelectedNc(null);
            setDirty(false);
          } catch {
            setSaveState('error');
            setTimeout(() => setSaveState('idle'), 3000);
          }
        },
      });
      return;
    }
    setConfirmState({
      content: renderBlockContent(hits),
      confirmLabel: t.formCancel,
      confirmColor: BTN_SECONDARY,
      onConfirm: () => setConfirmState(null),
    });
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (nc) => {
    if (scanningDelete) return;
    setScanningDelete(true);
    let hits = [];
    try { hits = await scanReferencesToEntity(org.id, 'nonconformity', nc.id); }
    catch { hits = []; }
    setScanningDelete(false);
    if (hits.length === 0) {
      setConfirmState({
        message: t.deleteConfirm,
        confirmLabel: t.deleteBtn,
        onConfirm: async () => {
          setConfirmState(null);
          setAllNCs(prev => (prev || []).filter(n => n.id !== nc.id));
          try {
            await deleteOrgFile(org.id, `nonconformities/${nc.id}.json`);
          } catch {
            setSaveState('error');
            setTimeout(() => setSaveState('idle'), 3000);
            setAllNCs(prev => [...(prev || []), nc]);
          }
        },
      });
      return;
    }
    setConfirmState({
      content: renderBlockContent(hits),
      confirmLabel: t.formCancel,
      confirmColor: BTN_SECONDARY,
      onConfirm: () => setConfirmState(null),
    });
  };

  // ── Form save → write directly to disk ────────────────────────────────────
  const handleFormSave = async (values) => {
    // §11 rule 34 — immutability. Once status=Closed, field updates are
    // rejected at the save entry. Reopen is only available via Settings.
    const existingForGuard = (allNCs || []).find(n => n.id === values.id);
    if (existingForGuard && isRecordImmutable(existingForGuard, 'nonconformity')) {
      setSaving(false);
      return;
    }
    setSaving(true);
    const now = new Date().toISOString();
    const changedBy = org.consultantPersonId || 'system';
    // eslint-disable-next-line no-unused-vars
    const { _isNew, ...rest } = values;
    const nc = {
      ...rest,
      orgId:      org.id,
      ref:        rest.ref.trim(),
      auditId:    rest.auditId.trim() || null,
      closedDate: rest.closedDate || null,
      verifiedBy: rest.verifiedBy || null,
      effectivenessReview: rest.effectivenessReview.trim() || null,
      effectivenessDate:   rest.effectivenessDate || null,
      updatedAt:  now,
    };
    const existingNc = _isNew ? null : (allNCs || []).find(n => n.id === nc.id);
    if (_isNew) {
      nc.changeLog = [];
    } else {
      nc.changeLog = appendChangeLog(existingNc, nc, changedBy);
    }
    try {
      await writeOrgFile(org.id, `nonconformities/${nc.id}.json`, nc);
      try {
        await syncBidirectionalRefs({
          orgId: org.id,
          entityId: nc.id,
          otherCollection: 'risks',
          otherIdsField: 'linkedNonconformityIds',
          previousOtherIds: existingNc?.linkedRiskIds || [],
          currentOtherIds: nc.linkedRiskIds,
          changedBy,
        });
      } catch (syncErr) {
        console.warn('[NonconformitiesView] bidirectional sync failed:', syncErr);
      }
      setAllNCs(prev => {
        const list = prev || [];
        const idx = list.findIndex(n => n.id === nc.id);
        if (idx >= 0) {
          const updated = [...list];
          updated[idx] = nc;
          return updated;
        }
        return [...list, nc];
      });
      setSaving(false);
      setFormInitial(prev => prev ? { ...prev, _isNew: false } : prev);
      setSelectedNc(nc);
      setDirty(false);
      if (pendingToListRef.current) {
        pendingToListRef.current = false;
        setView('list');
        setFormInitial(null);
        setSelectedNc(null);
      } else if (pendingAppSaveRef.current) {
        pendingAppSaveRef.current = false;
        onSaveResult?.(true);
      }
    } catch {
      pendingToListRef.current = false;
      if (pendingAppSaveRef.current) {
        pendingAppSaveRef.current = false;
        onSaveResult?.(false);
      }
      setSaving(false);
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 3000);
    }
  };

  // ── Close NC ──────────────────────────────────────────────────────────────
  const handleCloseNC = async (nc) => {
    const now = new Date().toISOString();
    const changedBy = org.consultantPersonId || 'system';
    const closed = { ...nc, status: 'Closed', closedDate: todayStr(), updatedAt: now };
    closed.changeLog = appendChangeLog(nc, closed, changedBy);
    try {
      await writeOrgFile(org.id, `nonconformities/${closed.id}.json`, closed);
      setAllNCs(prev => (prev || []).map(n => n.id === closed.id ? closed : n));
      setSelectedNc(closed);
    } catch {
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 3000);
    }
  };

  // ── Derived counters ──────────────────────────────────────────────────────
  const ncs         = useMemo(() => allNCs || [], [allNCs]);
  const cntOpen     = ncs.filter(n => n.status === 'Open').length;
  const cntInProg   = ncs.filter(n => n.status === 'In Progress').length;
  const cntClosed   = ncs.filter(n => n.status === 'Closed').length;
  const srcLabels   = SOURCE_LABELS[lang] || SOURCE_LABELS.en;

  // Distinct owners from loaded list.
  const ownerOptions = useMemo(() => {
    const set = new Set();
    ncs.forEach(n => { if (n.owner) set.add(n.owner); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [ncs]);

  // Distinct clauses / controls from loaded list (union of clauseRefs and linkedControlIds).
  const clauseControlOptions = useMemo(() => {
    const set = new Set();
    ncs.forEach(n => {
      (Array.isArray(n.clauseRefs) ? n.clauseRefs : []).forEach(v => set.add(v));
      (Array.isArray(n.linkedControlIds) ? n.linkedControlIds : []).forEach(v => set.add(v));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [ncs]);

  const filtered = ncs
    .filter(n => !filterStatus || n.status === filterStatus)
    .filter(n => !filterSource || n.source === filterSource)
    .filter(n => !filterOwner  || n.owner  === filterOwner)
    .filter(n => {
      if (filterClauseControl.length === 0) return true;
      const clauses = Array.isArray(n.clauseRefs) ? n.clauseRefs : [];
      const controls = Array.isArray(n.linkedControlIds) ? n.linkedControlIds : [];
      return filterClauseControl.some(v => clauses.includes(v) || controls.includes(v));
    })
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  // ── Selection ────────────────────────────────────────────────────────────
  const selectedInView = filtered.filter(n => selectedIds.has(n.id));
  const allChecked     = filtered.length > 0 && selectedInView.length === filtered.length;
  const someChecked    = selectedInView.length > 0;
  const toggleAll = (checked) => {
    if (checked) setSelectedIds(new Set(filtered.map(n => n.id)));
    else         setSelectedIds(new Set());
  };
  const toggleOne = (id, checked) => {
    setSelectedIds(prev => { const next = new Set(prev); if (checked) next.add(id); else next.delete(id); return next; });
  };
  const handleDeleteSelected = async () => {
    if (!selectedInView.length) return;
    if (scanningDelete) return;
    setScanningDelete(true);
    const items = [...selectedInView];
    let hitsLists;
    try {
      hitsLists = await Promise.all(items.map(n => scanReferencesToEntity(org.id, 'nonconformity', n.id)));
    } catch {
      hitsLists = items.map(() => []);
    }
    setScanningDelete(false);
    const deletable = [];
    const blocked   = [];
    items.forEach((n, i) => {
      if ((hitsLists[i] || []).length === 0) deletable.push(n);
      else blocked.push({ record: n, hits: hitsLists[i] });
    });
    const performBulkDelete = async (cohort) => {
      const deleted = new Set(cohort.map(n => n.id));
      setAllNCs(prev => (prev || []).filter(n => !deleted.has(n.id)));
      await Promise.all(cohort.map(n => deleteOrgFile(org.id, `nonconformities/${n.id}.json`).catch(() => {})));
      setSelectedIds(new Set());
    };
    if (blocked.length === 0) {
      const names = items.map(n => n.ref || n.id).join(', ');
      setConfirmState({
        message: t.deleteSelectedConfirm.replace('{names}', names),
        confirmLabel: t.deleteBtn,
        onConfirm: async () => {
          setConfirmState(null);
          await performBulkDelete(items);
        },
      });
      return;
    }
    const blockedSummaries = blocked.map(({ record, hits }) => ({
      label: record.ref || record.id,
      summary: summariseHits(hits, t),
    }));
    const allBlocked = deletable.length === 0;
    setConfirmState({
      content: renderBulkContent(allBlocked, deletable.length, blocked.length, items.length, blockedSummaries),
      confirmLabel: allBlocked ? t.formCancel : t.continueWithDeletables,
      confirmColor: allBlocked ? BTN_SECONDARY : BTN_DESTRUCTIVE_FILLED,
      onConfirm: async () => {
        setConfirmState(null);
        if (allBlocked) return;
        await performBulkDelete(deletable);
      },
    });
  };

  const btnSt = (primary) => ({
    ...FONT_SMALL,
    background: primary ? BTN_PRIMARY.background : 'none',
    color: primary ? BTN_PRIMARY.color : BTN_SECONDARY.color,
    border: primary ? 'none' : BTN_SECONDARY.border,
    borderRadius: 6, padding: '0.3rem 0.7rem', cursor: 'pointer',
    whiteSpace: 'nowrap',
  });

  return (
    <div>
      {/* ── Header bar ──────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.75rem',
      }}>
        <div>
          <h2 style={{
            ...FONT_PAGE_TITLE,
            color: TEXT_HEADING, margin: 0,
          }}>{t.title}</h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          {view === 'list' && (
            <button onClick={handleNew} style={{
              ...BTN_PRIMARY, ...FONT_BUTTON, border: 'none',
              borderRadius: 8, padding: '0.45rem 1.1rem', cursor: 'pointer',
            }}>{t.newNC}</button>
          )}
        </div>
      </div>

      {view === 'list' && saveState === 'error' && (
        <div style={{
          ...FONT_LABEL,
          background: ERROR_BG, color: ERROR_TEXT, border: `1px solid ${ERROR_BORDER}`,
          borderRadius: 6, padding: '0.35rem 0.75rem',
          marginBottom: '1rem',
        }}>{t.saveError}</div>
      )}

      {/* ── Summary bar ─────────────────────────────────────────────────── */}
      {view === 'list' && (
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          <SummaryCard value={ncs.length} label={t.cntTotal}      color={TEXT_HEADING} />
          <SummaryCard value={cntOpen}    label={t.cntOpen}       color={ERROR_TEXT} />
          <SummaryCard value={cntInProg}  label={t.cntInProgress} color={UNSAVED_AMBER} />
          <SummaryCard value={cntClosed}  label={t.cntClosed}     color={BRAND_PRIMARY} />
        </div>
      )}

      {/* ── Loading ─────────────────────────────────────────────────────── */}
      {allNCs === null && view === 'list' && (
        <p style={{ ...FONT_LABEL, color: TEXT_MUTED }}>
          {t.loading}
        </p>
      )}

      {/* ── LIST VIEW ───────────────────────────────────────────────────── */}
      {view === 'list' && allNCs !== null && (
        <>
          {/* Filter bar */}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterStatus}</span>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                style={{ ...inputSt(false), width: 'auto', padding: '0.3rem 0.5rem', fontSize: '0.78rem' }}>
                <option value="">{t.filterAll}</option>
                {NC_STATUSES.map(s => (
                  <option key={s} value={s}>{(NC_STATUS_LABELS[lang] || NC_STATUS_LABELS.en)[s] || s}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterSource}</span>
              <select value={filterSource} onChange={e => setFilterSource(e.target.value)}
                style={{ ...inputSt(false), width: 'auto', padding: '0.3rem 0.5rem', fontSize: '0.78rem' }}>
                <option value="">{t.filterAll}</option>
                {NC_SOURCES.map(s => (
                  <option key={s} value={s}>{srcLabels[s] || s}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterOwner}</span>
              <select value={filterOwner} onChange={e => setFilterOwner(e.target.value)}
                style={{ ...inputSt(false), width: 'auto', padding: '0.3rem 0.5rem', fontSize: '0.78rem' }}>
                <option value="">{t.filterAll}</option>
                {ownerOptions.map(o => (
                  <option key={o} value={o}>{resolvePerson(o, people)}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterClauseControl}</span>
              <MultiSelectFilter
                options={clauseControlOptions}
                values={filterClauseControl}
                onChange={setFilterClauseControl}
                allLabel={t.filterNoSelection}
                nSelectedTpl={t.filterNSelected}
              />
            </div>
          </div>

          {/* Empty state */}
          {ncs.length === 0 && (
            <div style={{
              textAlign: 'center', padding: '3rem 1.5rem',
              border: `1px dashed ${BORDER_DEFAULT}`, borderRadius: 12, background: BG_SURFACE,
            }}>
              <p style={{
                ...FONT_BODY_STRONG,
                color: TEXT_MUTED, margin: '0 0 0.4rem',
              }}>{t.emptyTitle}</p>
              <p style={{
                ...FONT_LABEL,
                color: TEXT_DISABLED, margin: 0,
              }}>{t.emptyHint}</p>
            </div>
          )}

          {/* Bulk action bar */}
          {someChecked && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.75rem', marginBottom: '0.6rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
              <span style={{ ...FONT_LABEL, color: BRAND_PRIMARY_DARK }}>
                {t.nSelected.replace('{n}', selectedInView.length)}
              </span>
              <button onClick={handleDeleteSelected} style={{ ...btnSt(false), color: BTN_DESTRUCTIVE_OUTLINE.color, border: BTN_DESTRUCTIVE_OUTLINE.border }}>{t.deleteSelected}</button>
            </div>
          )}

          {/* Table (homologated with RiskList style) */}
          {ncs.length > 0 && (
            <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${BORDER_DEFAULT}` }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: BG_SURFACE, minWidth: 700 }}>
                <thead>
                  <tr>
                    <th style={{ ...listThSt, width: 36, padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        title={t.selectAll}
                        checked={allChecked}
                        ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                        onChange={(e) => toggleAll(e.target.checked)}
                      />
                    </th>
                    {[t.colRef, t.colDate, t.colSource, t.colClause, t.colOwner, t.colStatus, t.colActions].map(col => (
                      <th key={col} style={listThSt}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(nc => {
                    const clauseControl = [
                      ...(Array.isArray(nc.clauseRefs) ? nc.clauseRefs : []),
                      ...(Array.isArray(nc.linkedControlIds) ? nc.linkedControlIds.map(id => `A.${id}`) : []),
                    ].join(' / ') || '—';
                    let ncDynBadge = null;
                    if (nc.status !== 'Closed') {
                      const cas = Array.isArray(nc.correctiveActions) ? nc.correctiveActions : [];
                      const openDeadlines = cas
                        .filter(ca => ca.status !== 'Completed' && ca.deadline)
                        .map(ca => dateStatus(ca.deadline));
                      if (openDeadlines.includes('overdue')) ncDynBadge = 'overdue';
                      else if (openDeadlines.includes('soon')) ncDynBadge = 'soon';
                    }
                    return (
                      <tr key={nc.id}>
                        <td style={{ ...listTdSt, textAlign: 'center', padding: '0.55rem 0.5rem' }}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(nc.id)}
                            onChange={(e) => toggleOne(nc.id, e.target.checked)}
                          />
                        </td>
                        <td style={{ ...listTdSt, ...FONT_LABEL, whiteSpace: 'nowrap' }}>
                          <span style={{ fontWeight: 500, color: TEXT_HEADING }}>{nc.ref}</span>
                        </td>
                        <td style={{ ...listTdSt, ...FONT_LABEL, whiteSpace: 'nowrap' }}>{nc.date || '—'}</td>
                        <td style={listTdSt}>{srcLabels[nc.source] || nc.source || '—'}</td>
                        <td style={{ ...listTdSt, ...FONT_LABEL }}>{clauseControl}</td>
                        <td style={{ ...listTdSt, whiteSpace: 'nowrap' }}>{resolvePerson(nc.owner, people) || '—'}</td>
                        <td style={listTdSt}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                            <NCStatusBadge status={nc.status} lang={lang} />
                            {ncDynBadge && (
                              <span style={{
                                ...FONT_TINY,
                                fontWeight: 600,
                                background: ncDynBadge === 'overdue' ? STATUS_BAD.bg : STATUS_WARN.bg,
                                color:      ncDynBadge === 'overdue' ? STATUS_BAD.text : STATUS_WARN.text,
                                borderRadius: 4,
                                padding: '0.15rem 0.45rem',
                                whiteSpace: 'nowrap',
                              }}>
                                {ncDynBadge === 'overdue' ? t.badgeOverdue : t.badgeSoon}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ ...listTdSt, whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', gap: '0.35rem' }}>
                            <button onClick={() => handleView(nc)} style={btnSt(false)}>{t.viewBtn}</button>
                            <button onClick={() => handleEdit(nc)} style={btnSt(false)}>{t.editBtn}</button>
                            <button onClick={() => handleDelete(nc)} style={{
                              ...btnSt(false),
                              color: BTN_DESTRUCTIVE_OUTLINE.color, border: BTN_DESTRUCTIVE_OUTLINE.border,
                            }}>{t.deleteBtn}</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── FORM VIEW ───────────────────────────────────────────────────── */}
      {view === 'form' && formInitial && (
        <NCForm
          t={t}
          lang={lang}
          orgId={org.id}
          aiProcessingConsent={org.aiProcessingConsent}
          initialValues={formInitial}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
          onDelete={handleDeleteFromForm}
          saveRequestTick={saveRequestTick}
          saving={saving}
          saveError={saveState === 'error'}
          dirty={dirty}
          people={people}
        />
      )}

      {/* ── DETAIL VIEW ─────────────────────────────────────────────────── */}
      {view === 'detail' && selectedNc && (
        <NCDetailView
          t={t}
          lang={lang}
          nc={selectedNc}
          onEdit={handleEdit}
          onBack={() => { setView('list'); setSelectedNc(null); }}
          onClose={handleCloseNC}
          people={people}
        />
      )}

      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        content={confirmState?.content}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
        cancelLabel={t.formCancel || 'Cancel'}
        confirmColor={confirmState?.confirmColor}
        secondaryAction={confirmState?.secondaryAction}
      />
    </div>
  );
}
